import { Action } from './base'
import { AggregatedUserModel, UserModel } from 'app/models/userModel'
import API from 'app/services/user/userApi'
import { UserInfoModel } from 'app/models/userInfoModel'
import { CompanyRole } from 'app/models/companyRole'
import { CompanyType } from 'app/constants/companyType'
import { RemoveUserRoleModel } from 'app/models/removeUserRoleModel'
import { flatten, uniqBy } from 'lodash'
import { RemoveUserFromCompanyModel } from 'app/models/removeUserFromCompany'
import { GroupRole } from 'app/models/groupRole'
import { KeyInputModel } from 'app/models/keyModel'
import { getRolesInfo } from 'app/common/companyRolesHelper'

export enum ActionTypeKeys {
  GET_USERS = 'GET_USERS',
  GET_PAGED_USERS = 'GET_PAGED_USERS',
  GET_COMPANY_USERS = 'GET_COMPANY_USERS',
  GET_USER_INFO = 'GET_USER_INFO',
  ADD_NEW_USER = 'ADD_NEW_USER',
  EDIT_USER = 'EDIT_USER',
  EDIT_USER_PROFILE = 'EDIT_USER_PROFILE',
  DELETE_USERS = 'DELETE_USERS',
  REMOVE_USER_FROM_COMPANY = 'REMOVE_USER_FROM_COMPANY',
  REMOVE_USER_ROLE = 'REMOVE_USER_ROLE',
  GET_USER = 'GET_USER',
  SEND_INVITATION_EMAIL = 'SEND_INVITATION_EMAIL',
  UNLOCK_USER = 'UNLOCK_USER',
}

export type GetUsersAction = Action<
  typeof ActionTypeKeys.GET_USERS,
  UserModel[]
>
export type GetPagedUsersAction = Action<
  typeof ActionTypeKeys.GET_PAGED_USERS,
  AggregatedUserModel
>
export type GetUserAction = Action<typeof ActionTypeKeys.GET_USER, UserModel>
export type GetCompanyUsersAction = Action<
  typeof ActionTypeKeys.GET_COMPANY_USERS,
  UserModel[]
>
export type GetUserInfoAction = Action<
  typeof ActionTypeKeys.GET_USER_INFO,
  UserInfoModel
>
export type CreateUserAction = Action<
  typeof ActionTypeKeys.ADD_NEW_USER,
  UserModel
>
export type EditUserAction = Action<typeof ActionTypeKeys.EDIT_USER, UserModel>
export type EditUserProfileAction = Action<
  typeof ActionTypeKeys.EDIT_USER_PROFILE,
  UserModel
>
export type DeleteUsersAction = Action<
  typeof ActionTypeKeys.DELETE_USERS,
  Array<string>
>
export type RemoveUserFromCompanyAction = Action<
  typeof ActionTypeKeys.REMOVE_USER_FROM_COMPANY,
  string
>
export type SendInvitationEmailAction = Action<
  typeof ActionTypeKeys.SEND_INVITATION_EMAIL,
  any
>
export type UnlockUserAction = Action<typeof ActionTypeKeys.UNLOCK_USER, any>

export const addUser = async (user: UserModel): Promise<any> => {
  const api = API.create()
  try {
    const response = await api.createUser(user, false)
    const data = response.content.data
    const addedUser: UserModel = mapUserFields(data)

    return addedUser
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const unlockUser = async (id: string): Promise<any> => {
  const api = API.create()
  try {
    const response = await api.unlockUser(id)
    const data = response.content.data

    return data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const createServiceUser = async (user: KeyInputModel): Promise<any> => {
  const api = API.create()
  try {
    const response = await api.createServiceUser(user, false)
    const data = response.content.data
    const addedUser: UserModel = mapUserFields(data)

    return addedUser
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const removeUserFromCompany = async (
  request: RemoveUserFromCompanyModel,
): Promise<any> => {
  const api = API.create()
  try {
    await api.removeFromCompany(request)
    return request.userId
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const editUser = async (user: UserModel): Promise<any> => {
  const api = API.create()
  try {
    const response = await api.editUser(user, false)
    const data = response.content.data
    const userInfo: UserModel = mapUserFields(data)

    return userInfo
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const editUserProfile = async (
  userProfile: UserInfoModel,
): Promise<any> => {
  const api = API.create()
  try {
    const response = await api.editUserProfile(userProfile, false)
    const data = response.content.data
    const userInfo: UserInfoModel = mapUserInfoFields(data)

    return userInfo
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const removeUserRole = async (
  request: RemoveUserRoleModel,
): Promise<any> => {
  const api = API.create()
  try {
    const response = await api.removeUserRole(request, false)

    return response.data
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const deleteUsers = async (ids: Array<string>): Promise<any> => {
  try {
    const api = API.create()
    await api.deleteUsers(ids)

    return ids
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getUserInfo = async (
  userId?: string,
  companyId?: string,
): Promise<any> => {
  const api = API.create()

  try {
    const reponse = await api.getUserInfo(userId, companyId)
    const data = reponse.content.data
    const userInfo: UserInfoModel = mapUserInfoFields(data)

    return userInfo
  } catch (error) {
    return { error: error.status.data.code }
  }
}

export const getUsers = async (
  companyId?: string,
  tenantId?: string,
  take?: number,
  skip?: number,
  orderBy?: string,
  descending?: boolean,
  searchValue?: string,
): Promise<any> => {
  const api = await API.create()
  const response = await api.getUsers(
    companyId,
    tenantId,
    take,
    skip,
    orderBy,
    descending,
    searchValue,
  )
  const data = response.content.data
  const users: Array<UserModel> = data.map((c) => mapUserFields(c))

  return users
}

export const getPagedUsers = async (
  companyId?: string,
  tenantId?: string,
  take?: number,
  skip?: number,
  orderBy?: string,
  descending?: boolean,
  searchValue?: string,
  onlyBlocked?: boolean,
): Promise<any> => {
  const api = await API.create()
  const response = await api.getPagedUsers(
    companyId,
    tenantId,
    take,
    skip,
    orderBy,
    descending,
    searchValue,
    onlyBlocked,
  )
  const data = response.content.data

  const users: Array<UserModel> = data.users.map((c) => mapUserFields(c))
  const aggregatedUsers: AggregatedUserModel = {
    users: users,
    usersCount: data.usersCount,
  }
  return aggregatedUsers
}

export const getUser = async (
  userId: string,
  companyId?: string,
): Promise<any> => {
  const api = await API.create()
  const reponse = await api.getUserInfo(userId, companyId)
  const data = reponse.content.data
  const user: UserModel = mapUserFields(data)

  return user
}

export async function sendInvitationEmail(login: string): Promise<boolean> {
  const api = API.create()

  const response = await api.sendInvitationEmail(login)
  return response.status === 200
}

const mergeUsers = (
  existingUsers: UserModel[],
  inheritedUsers: UserModel[],
  companyId?: string,
) => {
  let allUsers = [...existingUsers]
  for (var inheritedUser of inheritedUsers) {
    const currentUser = allUsers.find((u) => u.id === inheritedUser.id)
    if (!currentUser) {
      allUsers = [...allUsers, { ...inheritedUser, inherited: true }]
    } else {
      currentUser.roles = mergeUsersRoles(
        currentUser.roles,
        inheritedUser.roles,
        companyId,
      )
    }
  }
  return allUsers
}

const mergeUsersRoles = (
  existingRoles: CompanyRole[],
  inheritedRoles: CompanyRole[],
  companyId?: string,
) => {
  const allRole = existingRoles.find((c) => c.company.id === companyId)
  if (!allRole) return []
  const inheritedRolesFlat = uniqBy(
    flatten(inheritedRoles.map((r) => r.company.roles)),
    'id',
  )
  for (var inheritedRole of inheritedRolesFlat) {
    if (!allRole.company.roles.some((r) => r.id === inheritedRole.id)) {
      allRole.company.roles = [...allRole.company.roles, inheritedRole]
    }
  }
  return [allRole]
}

export const mapUserFields = (data: any, inherited?: boolean): UserModel => {
  const userData: UserModel = {
    id: data.id,
    login: data.login,
    roles:
      data.roles != null
        ? data.roles.map((r) => ({
            company: {
              id: r.companyId,
              name: r.companyName,
              roles: r.roles.map((ir) => ({ ...ir, inherited })),
            },
          }))
        : [],
    groups:
      data.groups != null
        ? data.groups.map((r) => ({
            group: {
              id: r.groupId,
              name: r.groupName,
            },
            roles: r.roles.map((ir) => ({ ...ir, inherited })),
          }))
        : [],
    fullName: `${data.firstName || '-'} ${data.surname || '-'}`,
    firstName: data.firstName,
    surname: data.surname,
    companyId: data.companyId,
    companyName: data.companyName,
    createdBy: data.createdBy,
    createdDate: data.createdDate,
    modifiedBy: data.modifiedBy,
    modifiedDate: data.modifiedDate,
    globalAdmin: data.globalAdmin,
    paymentAdmin: data.paymentAdmin,
    companyAdmin: data.companyAdmin,
    demoCompanyAdmin: data.demoCompanyAdmin,
    isDriver: data.isDriver,
    isActivated: data.isActivated,
    locked: data.locked,
    status: !data.isActivated ? 0 : data.locked ? 1 : 2,
  }
  const companyInfo = getRolesInfo(userData)
  userData.companyCount = companyInfo.length

  if (companyInfo.length > 1) {
    userData.aggregatedCompanyName = '...'
    userData.aggregatedRoles = '...'
  } else if (companyInfo.length == 0) {
    if (userData.companyName != null) {
      userData.aggregatedCompanyName = userData.companyName
    } else {
      userData.aggregatedCompanyName = ''
    }
    userData.aggregatedRoles = ''
  } else {
    userData.aggregatedCompanyName = companyInfo[0].object.name
    userData.aggregatedRoles = companyInfo[0].roles
      .map((r) => r.name)
      .filter((elem, pos, arr) => arr.indexOf(elem) == pos)
      .join(', ')
  }

  return userData
}

export const mapUserInfoFields = (data: any): UserInfoModel => {
  return {
    id: data.id,
    globalAdmin: data.globalAdmin,
    paymentAdmin: data.paymentAdmin,
    companyAdmin: data.companyAdmin,
    demoCompanyAdmin: data.demoCompanyAdmin,
    firstname: data.firstName,
    surname: data.surname,
    login: data.login,
    pesel: data.ssn,
    cardNumber: data.cardNumber,
    roles:
      data.roles != null
        ? data.roles.map(
            (r): CompanyRole => ({
              company: {
                id: r.companyId,
                name: r.companyName,
                type: r.isServiceCompany
                  ? CompanyType.SERVICE
                  : CompanyType.NORMAL,
                roles: r.roles,
              },
            }),
          )
        : [],
    groups:
      data.groups != null
        ? data.groups.map(
            (g): GroupRole => ({
              group: {
                id: g.groupId,
                name: g.groupName,
              },
              roles: g.roles,
            }),
          )
        : [],
    // Service-account members have no CompanyRole rows, so `roles` above is empty for them.
    // Without carrying this flag through, the empty list reads as "no access" and postLogin /
    // companySelection send them to the plan picker instead of their account's companies.
    hasServiceAccountAccess: data.hasServiceAccountAccess,
  }
}

export type UsersActions =
  | GetUsersAction
  | GetPagedUsersAction
  | GetCompanyUsersAction
  | GetUserInfoAction
  | CreateUserAction
  | EditUserAction
  | EditUserProfileAction
  | DeleteUsersAction
  | RemoveUserFromCompanyAction
  | SendInvitationEmailAction
  | UnlockUserAction
